import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, AlertCircle, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function TrialExpirationModal({
  isOpen,
  onConfirm,
  loading,
  trialPlan,
  fallbackPlan = "free",
}) {
  const [theme, setTheme] = useState("dark");
  const navigate = useNavigate();

  useEffect(() => {
    const savedTheme = localStorage.getItem("vite-ui-theme");
    setTheme(savedTheme === "light" ? "light" : "dark");
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className={`relative w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden ${
            theme === "dark"
              ? "bg-gray-900/95 text-gray-100 border border-gray-800"
              : "bg-white/95 text-gray-900 border border-gray-200"
          }`}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <button
            onClick={onConfirm}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-amber-500/20 text-amber-500 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="w-8 h-8" />
            </div>

            <h2 className="text-2xl font-bold mb-3">Trial Expired</h2>
            <p className="text-base text-gray-500 dark:text-white mb-8 leading-relaxed">
              Your trial for the{" "}
              <span className="font-bold text-foreground capitalize">
                {trialPlan || "Pro"}
              </span>{" "}
              plan has ended. You have been switched back to the{" "}
              <span className="capitalize">{fallbackPlan}</span> plan.
            </p>

            <div className="flex flex-col-reverse md:flex-row gap-3 w-full">
              <button
                onClick={onConfirm}
                disabled={loading}
                className="w-full py-3 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-white dark:hover:text-white transition-colors disabled:opacity-50 capitalize"
              >
                Continue with Current Plan ({fallbackPlan})
              </button>
              <button
                onClick={async () => {
                  await onConfirm();
                  navigate("/list-inventory");
                }}
                disabled={loading}
                className={`w-full py-3.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-lg font-semibold shadow-lg transition-all
                  hover:shadow-primary/25 hover:opacity-90 active:scale-[0.98]
                  ${loading ? "opacity-70 cursor-not-allowed" : ""}
                `}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Choose a Plan"
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
